import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  getSessionDecision,
  recomputeSessionDecision,
  type Decision,
  type SessionDecisionDetail,
} from '../../services/sessions';
import { listIncidentsBySession, getIncident, type Incident } from '../../services/incidents';
import { listEvents, type Event } from '../../services/events';
import {
  resumirIncidente,
  contagemLegivel,
  corDoTom,
  rotuloDoTom,
  humanizarEvento,
  type ResumoIncidente,
} from '../../lib/eventHumanizer';
import { AttackCategoryBadge } from '../../components/AttackCategoryBadge';
import { SeverityIndicator } from '../../components/SeverityIndicator';

interface Props {
  sessionUuid: string;
  // Hint pra UI antes do GET retornar — opcional. Não fica vazio quando aberto
  // pelo Sessions.tsx, mas o modal lida com null no caso isolado.
  initialHint?: {
    risk_score?: number;
    peak_risk_score?: number;
    decision?: Decision;
    decision_real?: Decision;
    user_id?: string;
    account_id?: string;
    hwid?: string;
  };
  onClose: () => void;
}

const decisionColors: Record<Decision, string> = {
  ALLOW:        'bg-emerald-900 text-emerald-300 border-emerald-700',
  FLAG:         'bg-amber-900 text-amber-300 border-amber-700',
  KICK:         'bg-orange-900 text-orange-300 border-orange-700',
  BLOCK:        'bg-red-900 text-red-300 border-red-700',
  FORCE_UPDATE: 'bg-blue-900 text-blue-300 border-blue-700',
};

function DecisionPill({ decision }: { decision: Decision }) {
  return (
    <span className={`text-xs px-2 py-1 rounded border font-mono ${decisionColors[decision]}`}>
      {decision}
    </span>
  );
}

function RiskScoreBig({ score }: { score: number }) {
  const pct = Math.min(100, Math.max(0, score));
  const color =
    score >= 86 ? 'bg-red-500' :
    score >= 61 ? 'bg-orange-500' :
    score >= 31 ? 'bg-amber-500' :
    'bg-emerald-500';
  const label =
    score >= 86 ? 'BLOCK'   :
    score >= 61 ? 'KICK'    :
    score >= 31 ? 'FLAG'    :
    'ALLOW';
  return (
    <div>
      <div className="flex items-baseline gap-3">
        <div className="text-4xl font-bold text-gray-100 tabular-nums">{score}</div>
        <div className="text-xs text-gray-500 uppercase tracking-wide">
          tier auto: <span className="text-gray-300">{label}</span>
        </div>
      </div>
      <div className="w-full h-2 bg-gray-800 rounded overflow-hidden mt-2">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] text-gray-600 mt-1">
        ADR-029 thresholds: ALLOW 0–30 · FLAG 31–60 · KICK 61–85 · BLOCK 86–100
      </div>
    </div>
  );
}

export function SessionDecisionModal({ sessionUuid, initialHint, onClose }: Props) {
  const [detail, setDetail] = useState<SessionDecisionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeNote, setRecomputeNote] = useState<string | null>(null);
  // Sessão 1 Frente A — aba Incidents
  const [activeTab, setActiveTab] = useState<'decision' | 'incidents' | 'events'>('decision');
  const [incidents, setIncidents] = useState<Incident[] | null>(null);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  // Resumo humano por incidente. A LISTA do backend não traz os tipos de
  // evento (só categoria e contagens), então o "o que aconteceu" precisa vir
  // do detalhe. Buscamos em paralelo — é uma sessão, são poucos incidentes.
  const [resumos, setResumos] = useState<Record<string, ResumoIncidente>>({});

  // Aba Eventos — o histórico BRUTO que acusa o jogador (é o que alimenta as
  // infrações). Escopo padrão = esta sessão; "Conta" amplia via user_id/account.
  const accountRef = initialHint?.user_id || initialHint?.account_id || null;
  const [eventsScope, setEventsScope] = useState<'session' | 'account'>('session');
  const [events, setEvents] = useState<Event[] | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsCursor, setEventsCursor] = useState<number>(0);
  const [eventsLoadingMore, setEventsLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRecomputeNote(null);
    getSessionDecision(sessionUuid)
      .then((r) => { if (!cancelled) setDetail(r); })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        if (!cancelled) setError(msg || 'erro ao carregar decisão');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionUuid]);

  // ESC fecha modal
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Sessão 1 Frente A — lazy load incidents quando tab incidents é clicada
  useEffect(() => {
    if (activeTab !== 'incidents' || incidents !== null) return;
    setIncidentsLoading(true);
    listIncidentsBySession(sessionUuid)
      .then((r) => {
        setIncidents(r.data);
        // Falha ao buscar um detalhe NÃO pode esconder o incidente: a linha
        // cai no fallback (categoria + contagens), que é o comportamento
        // antigo. Degradar é melhor que sumir.
        r.data.forEach((inc) => {
          getIncident(inc.id)
            .then((d) =>
              setResumos((prev) => ({
                ...prev,
                [inc.id]: resumirIncidente(d.evidence_events ?? []),
              })),
            )
            .catch(() => undefined);
        });
      })
      .catch(() => setIncidents([]))
      .finally(() => setIncidentsLoading(false));
  }, [activeTab, sessionUuid, incidents]);

  // Aba Eventos — monta o filtro conforme o escopo (sessão vs conta). O filtro
  // session_uuid casa contra Session.id (uuid interno), verificado na API.
  function buildEventsParams(cursor: number) {
    if (eventsScope === 'account' && accountRef) {
      return initialHint?.user_id
        ? { limit: 50, cursor, user_id: initialHint.user_id }
        : { limit: 50, cursor, account_id: initialHint?.account_id };
    }
    return { limit: 50, cursor, session_uuid: sessionUuid };
  }

  // Carrega a 1ª página ao abrir a aba e recarrega ao trocar de escopo.
  useEffect(() => {
    if (activeTab !== 'events') return;
    let cancelled = false;
    setEventsLoading(true);
    setEvents(null);
    listEvents(buildEventsParams(0))
      .then((r) => {
        if (cancelled) return;
        setEvents(r.data);
        setEventsCursor(r.next_cursor ?? 0);
      })
      .finally(() => { if (!cancelled) setEventsLoading(false); });
    return () => { cancelled = true; };
    // buildEventsParams fecha sobre eventsScope/sessionUuid/initialHint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, eventsScope, sessionUuid]);

  async function loadMoreEvents(): Promise<void> {
    if (!eventsCursor || eventsLoadingMore) return;
    setEventsLoadingMore(true);
    try {
      const r = await listEvents(buildEventsParams(eventsCursor));
      setEvents((prev) => {
        const seen = new Set((prev ?? []).map((e) => e.id));
        return [...(prev ?? []), ...r.data.filter((e) => !seen.has(e.id))];
      });
      setEventsCursor(r.next_cursor ?? 0);
    } finally {
      setEventsLoadingMore(false);
    }
  }

  async function handleRecompute(): Promise<void> {
    setRecomputing(true);
    setError(null);
    setRecomputeNote(null);
    try {
      const next = await recomputeSessionDecision(sessionUuid);
      setDetail(next);
      setRecomputeNote(`Recomputado às ${new Date().toLocaleTimeString()}`);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg || 'erro ao recomputar');
    } finally {
      setRecomputing(false);
    }
  }

  const view = detail ?? (initialHint
    ? {
        session_uuid: sessionUuid,
        risk_score:    initialHint.risk_score    ?? 0,
        decision:      initialHint.decision      ?? ('ALLOW' as Decision),
        decision_real: initialHint.decision_real ?? ('ALLOW' as Decision),
        decision_at: '',
      }
    : null);

  const betaModeActive = view ? view.decision !== view.decision_real : false;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Detalhes da decisão da sessão"
    >
      <div
        className="bg-[#161a1f] border border-[#2a2f36] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-[#2a2f36]">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">Sessão</h2>
            <code className="text-xs text-gray-500 font-mono break-all">{sessionUuid}</code>
            {initialHint?.account_id && (
              <div className="text-xs text-gray-400 mt-1">
                Conta: <strong className="text-gray-200">{initialHint.account_id}</strong>
                {initialHint.hwid && <span className="text-gray-600"> · HWID {initialHint.hwid}</span>}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 text-2xl leading-none w-8 h-8 flex items-center justify-center"
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        {/* Sessão 1 Frente A — tab bar */}
        <div className="flex border-b border-[#2a2f36]">
          <button
            type="button"
            onClick={() => setActiveTab('decision')}
            className={`px-5 py-2 text-sm border-b-2 transition ${
              activeTab === 'decision'
                ? 'border-red-500 text-gray-100'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            Decisão
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('incidents')}
            className={`px-5 py-2 text-sm border-b-2 transition ${
              activeTab === 'incidents'
                ? 'border-red-500 text-gray-100'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            Infrações {incidents !== null && `(${incidents.length})`}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('events')}
            className={`px-5 py-2 text-sm border-b-2 transition ${
              activeTab === 'events'
                ? 'border-red-500 text-gray-100'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            Eventos {events !== null && `(${events.length}${eventsCursor > 0 ? '+' : ''})`}
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading && !view && activeTab === 'decision' && (
            <p className="text-gray-500 text-sm">Carregando decisão…</p>
          )}

          {error && (
            <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2">
              {error}
            </div>
          )}

          {activeTab === 'decision' && view && (
            <>
              <section>
                <div className="text-xs uppercase text-gray-500 tracking-wide mb-2">
                  Risk score
                </div>
                <RiskScoreBig score={view.risk_score} />
                <div className="mt-2 text-xs text-gray-400">
                  Pico histórico da sessão:{' '}
                  <strong className="text-orange-300 text-base tabular-nums">
                    {initialHint?.peak_risk_score ?? view.risk_score}
                  </strong>
                  <span className="text-gray-600 ml-2">o pico não diminui com o decay temporal</span>
                </div>
              </section>

              <section className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase text-gray-500 tracking-wide mb-2">
                    Decisão aplicada
                  </div>
                  <DecisionPill decision={view.decision} />
                  {betaModeActive && (
                    <p className="text-[10px] text-amber-400 mt-1">
                      ⚠️ BETA_MODE=log_only — decisão real seria diferente
                    </p>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500 tracking-wide mb-2">
                    Decisão real (sem beta)
                  </div>
                  <DecisionPill decision={view.decision_real} />
                </div>
              </section>

              <section>
                <div className="text-xs uppercase text-gray-500 tracking-wide mb-1">
                  Computado em
                </div>
                <div className="text-sm text-gray-300 font-mono">
                  {view.decision_at
                    ? new Date(view.decision_at).toLocaleString()
                    : <span className="text-gray-500 italic">nunca computado — clique em "Recompute"</span>}
                </div>
              </section>

              {recomputeNote && (
                <div className="bg-emerald-950 border border-emerald-800 text-emerald-300 text-xs rounded px-3 py-2">
                  {recomputeNote}
                </div>
              )}
            </>
          )}

          {/* Sessão 1 Frente A — aba Incidents */}
          {activeTab === 'incidents' && (
            <section>
              {incidentsLoading && <p className="text-gray-500 text-sm">Carregando incidents…</p>}
              {!incidentsLoading && incidents && incidents.length === 0 && (
                <p className="text-gray-500 text-sm">Nenhum incident pra esta sessão.</p>
              )}
              {!incidentsLoading && incidents && incidents.length > 0 && (
                <ul className="space-y-2">
                  {incidents.map((i) => (
                    <li key={i.id} className="bg-[#0d0f12] border border-[#2a2f36] rounded p-3">
                      {/* O QUE ACONTECEU vem primeiro. Categoria, severidade
                          e contagens descem pra linha de apoio — são
                          classificação, não narrativa. */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm text-gray-100 font-medium leading-snug">
                            {resumos[i.id]?.titulo ?? 'Carregando o que aconteceu…'}
                          </p>
                          {/* Demais eventos do mesmo incidente, agrupados. */}
                          {resumos[i.id]?.linhas.slice(1).map((l) => (
                            <p key={l.titulo} className="text-xs text-gray-400 mt-0.5">
                              {l.vezes > 1 ? `${l.vezes}× ` : ''}
                              {l.titulo}
                            </p>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {resumos[i.id] && (
                            <span
                              className={`text-[11px] px-2 py-0.5 rounded border ${corDoTom(resumos[i.id].tom)}`}
                            >
                              {rotuloDoTom(resumos[i.id].tom)}
                            </span>
                          )}
                          <Link
                            to={`/incidents/${i.id}`}
                            className="text-xs text-blue-400 hover:underline"
                            onClick={onClose}
                          >
                            Detalhes →
                          </Link>
                        </div>
                      </div>
                      {/* Incidente que só tem ruído operacional: dizer isso na
                          cara evita o operador perder tempo investigando. */}
                      {resumos[i.id]?.soOperacional && (
                        <p className="text-[11px] text-gray-500 mt-1 italic">
                          Nada aqui é conduta do jogador — é ruído do sistema.
                        </p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap mt-2">
                        <AttackCategoryBadge category={i.attack_category} />
                        <SeverityIndicator severity={i.severity_max} />
                        <span className="text-xs text-gray-500">
                          {contagemLegivel(i.event_count, i.occurrence_sum)}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-400">
                          {i.status === 'open' ? 'em aberto' : i.status}
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-600 mt-1">
                        {new Date(i.first_seen_at).toLocaleString()} até{' '}
                        {new Date(i.last_seen_at).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {/* Aba Eventos — histórico bruto por sessão/conta */}
          {activeTab === 'events' && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-gray-500">Escopo:</span>
                <button
                  type="button"
                  onClick={() => setEventsScope('session')}
                  className={`text-xs px-2 py-1 rounded border ${
                    eventsScope === 'session'
                      ? 'border-red-500 text-gray-100 bg-[#0d0f12]'
                      : 'border-[#2a2f36] text-gray-400 hover:text-gray-200'
                  }`}
                >
                  Esta sessão
                </button>
                <button
                  type="button"
                  disabled={!accountRef}
                  onClick={() => setEventsScope('account')}
                  title={accountRef ? `Todos os eventos de ${accountRef}` : 'Sessão sem conta identificada'}
                  className={`text-xs px-2 py-1 rounded border disabled:opacity-40 ${
                    eventsScope === 'account'
                      ? 'border-red-500 text-gray-100 bg-[#0d0f12]'
                      : 'border-[#2a2f36] text-gray-400 hover:text-gray-200'
                  }`}
                >
                  Conta{accountRef ? ` (${accountRef})` : ''}
                </button>
              </div>

              {eventsLoading && <p className="text-gray-500 text-sm">Carregando eventos…</p>}
              {!eventsLoading && events && events.length === 0 && (
                <p className="text-gray-500 text-sm">
                  Nenhum evento {eventsScope === 'account' ? 'para esta conta' : 'para esta sessão'}.
                </p>
              )}
              {!eventsLoading && events && events.length > 0 && (
                <>
                  <ul className="space-y-2">
                    {events.map((e) => {
                      const h = humanizarEvento(e.event_type, e.payload);
                      return (
                        <li key={e.id} className="bg-[#0d0f12] border border-[#2a2f36] rounded p-3">
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <p className="text-sm text-gray-100 font-medium leading-snug">{h.titulo}</p>
                            <span className={`text-[11px] px-2 py-0.5 rounded border shrink-0 ${corDoTom(h.tom)}`}>
                              {rotuloDoTom(h.tom)}
                            </span>
                          </div>
                          {h.detalhe && <p className="text-xs text-gray-400 mt-0.5">{h.detalhe}</p>}
                          <div className="flex items-center gap-2 flex-wrap mt-2">
                            {e.attack_category && <AttackCategoryBadge category={e.attack_category} />}
                            <SeverityIndicator severity={e.severity} />
                            <code className="text-[11px] text-gray-500">{e.event_type}</code>
                            {e.occurrence_count && e.occurrence_count > 1 && (
                              <span
                                className="text-[11px] px-2 py-0.5 rounded bg-purple-900 text-purple-200"
                                title="Ocorrências agregadas na janela"
                              >
                                ×{e.occurrence_count}
                              </span>
                            )}
                            {e.source_program?.name && (
                              <span
                                className="text-[11px] px-2 py-0.5 rounded bg-rose-900 text-rose-200"
                                title={e.source_program.signer_subject || ''}
                              >
                                🎯 {e.source_program.name}
                              </span>
                            )}
                            {e.action_taken && e.action_taken !== 'log' && (
                              <span className="text-[11px] px-2 py-0.5 rounded bg-gray-800 text-gray-300">
                                ação: {e.action_taken}
                              </span>
                            )}
                            {typeof e.identity_epoch === 'number' && e.identity_epoch > 0 && (
                              <span
                                className="text-[11px] px-2 py-0.5 rounded bg-amber-900 text-amber-200 border border-amber-700"
                                title="A sessão trocou de identidade — outra pessoa no mesmo PC (lan house)"
                              >
                                época {e.identity_epoch}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-gray-600 mt-1">
                            {new Date(e.last_seen_at || e.created_at).toLocaleString()}
                            {e.occurrence_count && e.occurrence_count > 1 && e.first_seen_at && (
                              <span> · desde {new Date(e.first_seen_at).toLocaleString()}</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {events.length} evento{events.length === 1 ? '' : 's'} carregado{events.length === 1 ? '' : 's'}
                    </span>
                    {eventsCursor > 0 ? (
                      <button
                        type="button"
                        onClick={() => void loadMoreEvents()}
                        disabled={eventsLoadingMore}
                        className="px-3 py-1.5 rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 hover:bg-[#0d0f12] disabled:opacity-50"
                      >
                        {eventsLoadingMore ? 'Carregando…' : 'Carregar mais'}
                      </button>
                    ) : (
                      <span className="text-gray-600">fim da lista</span>
                    )}
                  </div>
                </>
              )}
            </section>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#2a2f36] bg-[#0d0f12]">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-400 hover:text-gray-200 px-3 py-1.5"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={() => void handleRecompute()}
            disabled={recomputing || loading}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded px-4 py-1.5"
          >
            {recomputing ? 'Recomputando…' : '↻ Recompute decision'}
          </button>
        </footer>
      </div>
    </div>
  );
}
